import { getLogicNodeSchema } from '../Hasp/properties/v2/logic/index.js';
import { getCommandDefinition } from '../actions/commandRegistry.js';
import { serializeCommandAction } from '../actions/commandSerializer.js';

const objectRef = target => {
    const pageId = Number(target?.pageId);
    const objectId = Number(target?.objectId);
    return Number.isInteger(pageId) && Number.isInteger(objectId) ? `p${pageId}b${objectId}` : null;
};

/** Display form of an event name: `swipe_left` → `swipe left`. */
export function eventLabel(event) {
    return String(event ?? '').replace(/_/g, ' ');
}

/**
 * Short, human-readable title and summary of a node, for the canvas and
 * outlines. Never authoritative: validation lives in the model.
 */
export function describeLogicNode(node) {
    const schema = getLogicNodeSchema(node?.type);
    const data = node?.data ?? {};
    const base = { icon: schema?.icon ?? '?', color: schema?.color ?? '#64748b', title: schema?.label ?? node?.type ?? '?' };
    switch (node?.type) {
    case 'event':
        return { ...base, summary: data.event ? `on ${eventLabel(data.event)}` : 'Select an event' };
    case 'command': {
        const definition = getCommandDefinition(data.commandId);
        if (!definition) return { ...base, summary: 'Select a command' };
        return { ...base, title: definition.label, summary: serializeCommandAction(data) ?? 'Incomplete parameters' };
    }
    case 'setObjectProperty': {
        const ref = objectRef(data.target);
        return { ...base, summary: ref && data.property?.name ? `${ref}.${data.property.name}=${data.value ?? ''}` : 'Select a target' };
    }
    case 'templateVisibility': {
        const templateId = data.target?.templateId;
        return {
            ...base,
            title: data.operation === 'hide' ? 'Template → Hide' : 'Template → Show',
            summary: templateId !== undefined && templateId !== null ? `T${templateId}` : 'Select a template',
        };
    }
    case 'model3dLight': {
        const ref = objectRef(data.target);
        return { ...base, summary: ref && data.light ? `${ref} ${data.light}.${data.property}` : 'Select a 3D object' };
    }
    case 'gpio':
        return { ...base, summary: data.entity || 'Select an entity' };
    case 'gpioBinding':
        return { ...base, summary: data.property ? `${data.property} → ${data.target_property || '?'}` : 'Select a property' };
    case 'condition': {
        const parts = (data.conditions ?? []).map(item => `${item.property || '?'} ${item.operator ?? '=='} ${item.value ?? ''}`.trim());
        return { ...base, summary: parts.length ? parts.join(' and ') : 'Add a condition' };
    }
    case 'switch':
        return { ...base, summary: data.property ? `${data.property}: ${(data.cases ?? []).length} case(s)` : 'Select a property' };
    case 'gpioEntity':
        return { ...base, summary: data.entity ? `${data.entity}${data.property ? `.${data.property}` : ''}` : 'Select an entity' };
    default:
        return { ...base, summary: '' };
    }
}
